import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { isEmpty, isNil, keyBy } from 'lodash';

import { navigate } from 'libs/router';

import Tooltip from 'components/Tooltip';
import ActionButton from 'components/buttons/action-button/ActionButton';
import CroppedProfileImage from 'components/cropped-profile-image/CroppedProfileImage';
import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';
import { withModal } from 'components/modal/ModalHOC';
import ActionLink from 'components/buttons/action-link/ActionLink';
import ResponseTimeInfo from 'components/response-time-info/ResponseTimeInfo';
import AgencyCreatorsModal from './agency-creators-modal/AgencyCreators';
import CategoryPill from 'components/category-pill/CategoryPill';

import InfluencerProfileSummaryChannels from './channels/InfluencerProfileSummaryChannels';

import { uploadImage } from '../../reducers/influencerImages';
import { fetchInfluencerResponseTime, fetchInfluencerSelfResponseTime } from 'modules/influencers/reducers/influencerResponseTime';

import { __t, applyTranslations } from 'utils/language';
import {
  getExtraChannelsFromChannelsObject
} from 'utils/channels';
import { getEntityFromProfile, getImageFallback } from 'utils/view';
import { getAgeString, getInfluencerFullName, getInfluencerLocationsPreview } from 'utils/influencers';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { isAdmin } from 'utils/account';

import './influencer-profile-summary.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./influencer-profile-summary.i18n.json'));

import type {
  Account,
  Image,
  InfluencerProfile,
  InfluencerProfileImage,
  InfluencerSummaryImage,
  Organization,
  ResponseTime,
  Role
} from 'types';

type OwnProps = {
  influencerProfile: InfluencerProfile,
  actionComponent: any,
  canEdit: boolean,
  inEditMode: boolean,
  haveMetricsBeenGathered: boolean
};

type StateProps = {
  currentAccount: Account,
  roles: Array<Role>,
  currentOrganization: Organization,
  influencerImage: InfluencerSummaryImage,
  responseTime?: ResponseTime | null
};

type DispatchProps = {
  dispatchUploadImage: (profile: InfluencerProfile, type: string, image: Image) => Promise<Image>,
  dispatchFetchInfluencerResponseTime: (profileId: number) => void,
  dispatchFetchInfluencerSelfResponseTime: (profileId: number) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'influencer_profile_summary';

const ActionLinkWithModal = withModal(ActionLink, {
  modalContent: AgencyCreatorsModal
});

class InfluencerProfileSummary extends React.PureComponent<Props> {
  componentDidMount() {
    const {
      influencerProfile,
      currentOrganization,
      dispatchFetchInfluencerResponseTime,
      dispatchFetchInfluencerSelfResponseTime
    } = this.props;
    const isBrand = isBrandsFocusedOrganization(currentOrganization);

    if (isBrand) {
      dispatchFetchInfluencerResponseTime(influencerProfile.id);
    }

    if (!isBrand) {
      dispatchFetchInfluencerSelfResponseTime(influencerProfile.id);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { currentOrganization, dispatchFetchInfluencerResponseTime } = this.props;
    const isBrand = isBrandsFocusedOrganization(currentOrganization);

    if (prevProps.influencerProfile.id !== this.props.influencerProfile.id && isBrand) {
      dispatchFetchInfluencerResponseTime(this.props.influencerProfile.id);
    }
  }

  getProfileImage(): InfluencerProfileImage {
    const { influencerProfile, influencerImage } = this.props;
    const entity = getEntityFromProfile(influencerProfile);
    const imageFallback = getImageFallback(entity);

    return {
      image: influencerImage.profileImage,
      fallback: imageFallback
    };
  }

  handleImageChanged = (image: Image) => {
    const { influencerProfile, dispatchUploadImage } = this.props;

    return dispatchUploadImage(influencerProfile, 'profile', image);
  };

  renderAvatar(): React.ReactNode {
    const { inEditMode } = this.props;
    const profileImage = this.getProfileImage();

    return (
      <div className="influencer-profile-summary__avatar">
        <CroppedProfileImage
          image={profileImage.image}
          canEdit={inEditMode}
          type="profile"
          customSize={{ width: 152, height: 152 }}
          onChange={this.handleImageChanged}
        />
      </div>
    );
  }

  renderButton() {
    const { influencerProfile } = this.props;

    return (
      <ActionButton
        color="primary"
        className="influencer-profile-summary__button"
        onClick={() => {
          navigate.to(`/content-creator/profiles/${influencerProfile.id}/edit`);
        }}
      >
        {__t(`${MSG_KEY}.labels.edit_profile`)}
      </ActionButton>
    );
  }

  renderAgency(): React.ReactNode | undefined | null {
    const { influencerProfile } = this.props;
    const agencyName = influencerProfile.agencyDetails && influencerProfile.agencyDetails.name;
    const { isAgencyManaged } = influencerProfile;
    const agencyTitle = __t(`${MSG_KEY}.labels.agency`);

    return isAgencyManaged && !isNil(agencyName) && (
      <div
        className="influencer-profile-summary__agency text-overflow-ellipsis"
        title={`${agencyTitle}: ${agencyName}`}
      >
        {agencyTitle}:&nbsp;
        <span className="influncer-profile-summary__text influncer-profile-summary__agency-title">
          <ActionLinkWithModal
            size="normal"
            color="secondary"
            className="influencer-profile-summary__agency-link"
            modalContentProps={{
              influencerProfile,
              agencyName
            }}
          >
            {agencyName}
          </ActionLinkWithModal>
        </span>
      </div>
    );
  }

  renderLocation(): React.ReactNode {
    const { influencerProfile } = this.props;
    const locations = getInfluencerLocationsPreview(influencerProfile.locations);
    const locationTitle = __t(`${MSG_KEY}.labels.location`);
    const location = locations[0];

    return location && (
      <div
        className="influencer-profile-summary__location text-overflow-ellipsis"
        title={`${locationTitle}: ${location}`}
      >
        {locationTitle}:&nbsp;
        <span className="influncer-profile-summary__text">
          {location}
          {
            locations.length > 1 && (
              <Tooltip
                message={`${locations.slice(1).join('<br />')}`}
                width="auto-compact"
              >
                &nbsp;{`(+${locations.length - 1} ${__t(`${MSG_KEY}.labels.more`)})`}
              </Tooltip>
            )
          }
        </span>
      </div>
    );
  }

  renderVerificationLabel(): React.ReactNode {
    const { influencerProfile } = this.props;

    return (
      <div className="influencer-profile-summary__verification-label">
        <InfluencerVerificationLabel
          withMargin
          type={influencerProfile.isLightweight ? 'unverified' : 'verified'}
        />
      </div>
    );
  }

  renderAge(): React.ReactNode | undefined | null {
    const { influencerProfile } = this.props;
    const ageString = getAgeString(influencerProfile.influencerAge, influencerProfile.ageRange);

    return ageString && (
      <div
        className="influencer-profile-summary__age"
        title={ageString}
      >
        <span className="influncer-profile-summary__text">{ageString}</span>
      </div>
    );
  }

  renderGender(): React.ReactNode | undefined | null {
    const { influencerProfile } = this.props;
    const gender = influencerProfile.influencerGender;
    const genderLowercase = gender ? gender.toLowerCase() : '';
    const genderLabel = __t(`${COMMON_MSG_KEY}.gender.${genderLowercase}`);

    return genderLowercase && genderLabel && (
      <div
        className="influencer-profile-summary__gender"
        title={genderLabel}
      >
        <span className="influncer-profile-summary__text">{genderLabel}</span>
      </div>
    );
  }

  renderFullName(): React.ReactNode {
    const { influencerProfile } = this.props;
    const influencerName = influencerProfile.name || '';
    const influencerFullName = getInfluencerFullName(influencerProfile);

    return influencerFullName.length > 0 && influencerFullName !== influencerName && (
      <div className="influencer-profile-summary__full-name">
        <div
          className="text-overflow-ellipsis"
          title={influencerFullName}
        >
          <span className="influncer-profile-summary__text">
            {influencerFullName}
          </span>
        </div>
      </div>
    );
  }

  renderName(): React.ReactNode {
    const { influencerProfile } = this.props;
    const influencerName = influencerProfile.name || '';

    return (
      <div
        className="influencer-profile-summary__name"
        title={influencerName}
      >
        <div className="influencer-profile-summary__name-value text-overflow-ellipsis">
          {influencerName}
        </div>
        {
          influencerProfile &&
            this.renderVerificationLabel()
        }
      </div>
    );
  }

  renderFooter(): React.ReactNode {
    const {
      currentAccount,
      actionComponent,
      canEdit,
      inEditMode,
      influencerProfile,
      responseTime,
      roles
    } = this.props;

    const actionClasses = classnames('influencer-profile-summary__footer-action', {
      'influencer-profile-summary__footer-action--admin': isAdmin(currentAccount, roles)
    });

    return (
      <div className="influencer-profile-summary__footer">
        <div className="influencer-profile-summary__footer-response-time">
          <div className="influencer-profile-summary__footer-response-time-wrapper">
            <ResponseTimeInfo
              responseTime={responseTime}
              creatorProfile={influencerProfile}
            />
          </div>
        </div>
        {
          actionComponent && actionComponent.length !== 0 && (
            <div className={actionClasses}>
              {actionComponent}
            </div>
          )
        }
        {
          canEdit && !inEditMode &&
            this.renderButton()
        }
      </div>
    );
  }

  renderChannels(): React.ReactNode | undefined | null {
    const { influencerProfile, haveMetricsBeenGathered } = this.props;

    if (isNil(influencerProfile) || isNil(influencerProfile.channels)) {
      return null;
    }

    if (!haveMetricsBeenGathered) {
      return (
        <div className="influencer-profile-summary__gathering-data">
          {__t(`${MSG_KEY}.messages.gathering_data`)}
        </div>
      );
    }

    const channelsObject = keyBy(influencerProfile.channels, 'type');
    const additionalChannels = getExtraChannelsFromChannelsObject(channelsObject);

    return (
      <React.Fragment>
        <div className="influencer-profile-summary__channels influencer-profile-summary__channels--type-profile">
          <InfluencerProfileSummaryChannels
            type="profile"
            channels={influencerProfile.channels}
          />
        </div>
        {
          !isEmpty(additionalChannels) && (
            <div className="influencer-profile-summary__channels influencer-profile-summary__channels--type-additional">
              <InfluencerProfileSummaryChannels
                type="additional"
                channels={additionalChannels}
              />
            </div>
          )
        }
      </React.Fragment>
    );
  }

  renderSpecialization = (): React.ReactNode => {
    const { influencerProfile } = this.props;
    const values = influencerProfile.specializedIn?.custom ?? [];

    if (isEmpty(values)) {
      return null;
    }

    return (
      <div className="influencer-profile-summary__specialization">
        <div className="influencer-profile-summary__specialization-label">
          {__t(`${MSG_KEY}.labels.specialization`)}:
        </div>
        <div className="influencer-profile-summary__specialization-values">
          {
            values.map(value => (
              <CategoryPill
                key={`influencer-profile-summary__specialization-value--${value}`}
                label={__t(`collabary_common.specialized_in.${value}`)}
              />
            ))
          }
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { haveMetricsBeenGathered } = this.props;

    return (
      <div
        className="influencer-profile-summary"
        data-qa="influencer-profile-summary"
      >
        <div className="influencer-profile-summary__body">
          {this.renderAvatar()}
          <div className="influencer-profile-summary__influencer">
            {this.renderName()}
            <div className="influencer-profile-summary__details">
              <div className="influencer-profile-summary__details--primary">
                {
                  haveMetricsBeenGathered && (
                    <React.Fragment>
                      {this.renderFullName()}
                      {this.renderAge()}
                      {this.renderGender()}
                    </React.Fragment>
                  )
                }
                {this.renderLocation()}
                {this.renderAgency()}
              </div>
              <div className="influencer-profile-summary__channels_all">
                {this.renderChannels()}
              </div>
              {this.renderSpecialization()}
            </div>
          </div>
        </div>
        {this.renderFooter()}
      </div>
    );
  }
}

const mapStateToProps = (
  {
    influencerImages,
    account,
    influencerResponseTime,
    organizations
  }, { influencerProfile }
): StateProps => ({
  influencerImage: influencerImages,
  currentOrganization: organizations.currentOrganization ?? {},
  currentAccount: account.currentAccount,
  roles: account.roles,
  responseTime: influencerResponseTime.responseTimeData[influencerProfile.id]
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUploadImage: (influencerProfile: InfluencerProfile, type: string, image: Image) => (
    dispatch(uploadImage(influencerProfile, type, image))
  ),
  dispatchFetchInfluencerResponseTime: (profileId: number) =>
    dispatch(fetchInfluencerResponseTime([profileId])),
  dispatchFetchInfluencerSelfResponseTime: (profileId: number) => (
    dispatch(fetchInfluencerSelfResponseTime(profileId))
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(InfluencerProfileSummary);
